import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import PoPShell from '../PoPShell';
import { usePoPAuth } from '../../../context/PoPAuthContext';
import { popLogin } from '../../../lib/popApi';
import { capGetProject, capListTasks, capListReviews, capListRisks, capListDocs } from '../../../lib/capstoneApi';
import { StatusBadge, ProgressRing, btnAmber } from '../../../components/capstone/shared';
import OverviewTab from '../../../components/capstone/OverviewTab';
import TasksTab from '../../../components/capstone/TasksTab';
import TeamTab from '../../../components/capstone/TeamTab';
import DocsTab from '../../../components/capstone/DocsTab';
import FeedbackTab from '../../../components/capstone/FeedbackTab';
import RiskTab from '../../../components/capstone/RiskTab';
import ExportTab from '../../../components/capstone/ExportTab';

const TABS = [
  ['overview', 'Overview'],
  ['tasks', 'Milestones & Tasks'],
  ['team', 'Team'],
  ['docs', 'Documents'],
  ['feedback', 'Mentor Feedback'],
  ['risks', 'Risk Log'],
  ['export', 'Reports & Export'],
];

export default function CapstoneWorkspace() {
  const { id } = useParams();
  const { user, loading } = usePoPAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'overview';

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [risks, setRisks] = useState([]);
  const [docs, setDocs] = useState([]);
  const [err, setErr] = useState('');

  const reloadProject = useCallback(() => capGetProject(id).then(setProject).catch((e) => setErr(e.response?.data?.detail || 'Could not load project')), [id]);
  const reloadTasks = useCallback(() => capListTasks(id).then((r) => setTasks(r.tasks)).catch(() => {}), [id]);
  const reloadReviews = useCallback(() => capListReviews(id).then((r) => setReviews(r.reviews)).catch(() => {}), [id]);
  const reloadRisks = useCallback(() => capListRisks(id).then((r) => setRisks(r.risks)).catch(() => {}), [id]);
  const reloadDocs = useCallback(() => capListDocs(id).then((r) => setDocs(r.documents)).catch(() => {}), [id]);

  useEffect(() => {
    if (loading || !user) return;
    reloadProject(); reloadTasks(); reloadReviews(); reloadRisks(); reloadDocs();
  }, [user, loading, reloadProject, reloadTasks, reloadReviews, reloadRisks, reloadDocs]);

  if (!loading && !user) {
    return (
      <PoPShell activeTab="capstone">
        <div className="max-w-md mx-auto text-center py-24">
          <p className="text-sm text-white/60 mb-5">Sign in to open this capstone workspace.</p>
          <button className={btnAmber} onClick={popLogin}>Sign in</button>
        </div>
      </PoPShell>
    );
  }

  if (err) {
    return (
      <PoPShell activeTab="capstone">
        <div className="text-center py-24 text-white/60 text-sm" data-testid="cap-workspace-error">{err}</div>
      </PoPShell>
    );
  }

  if (!project) {
    return (
      <PoPShell activeTab="capstone">
        <div className="flex items-center justify-center py-24 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading workspace…</div>
      </PoPShell>
    );
  }

  const common = { project, tasks, reviews, risks, docs, reloadProject, reloadTasks, reloadReviews, reloadRisks, reloadDocs, user };

  return (
    <PoPShell activeTab="capstone">
      <button onClick={() => navigate('/pop/capstone')} className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-5">
        <ArrowLeft className="w-3.5 h-3.5" /> All projects
      </button>

      <div className="flex items-start justify-between gap-5 flex-wrap mb-6" data-testid="cap-workspace-header">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-amber-300/80 mb-1.5">{project.domain}{project.sponsor_org ? ` · ${project.sponsor_org}` : ''}</div>
          <h1 className="font-serif text-2xl sm:text-3xl leading-tight">{project.title}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2.5">
            <StatusBadge status={project.status} testid="cap-project-status" />
            {project.days_left !== null && <span className="text-[11px] text-white/50">{project.days_left >= 0 ? `${project.days_left} days to deadline` : `${Math.abs(project.days_left)} days overdue`}</span>}
            <span className="text-[11px] text-white/40">Your role: <span className="text-white/70 font-semibold capitalize">{project.my_role}</span></span>
            {project.ai_assist_pct > 0 && <span className="text-[10.5px] text-amber-200/60">✨ {project.ai_assist_pct}% AI-assisted</span>}
          </div>
        </div>
        <ProgressRing pct={project.progress_pct} size={72} />
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1 mb-7 border-b border-white/8" data-testid="cap-workspace-tabs">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            data-testid={`cap-tab-${key}`}
            onClick={() => setParams({ tab: key })}
            className={`px-3.5 py-2 rounded-t-lg text-[12.5px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
              tab === key ? 'text-amber-300 border-amber-300' : 'text-white/55 border-transparent hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab {...common} />}
      {tab === 'tasks' && <TasksTab {...common} />}
      {tab === 'team' && <TeamTab {...common} />}
      {tab === 'docs' && <DocsTab {...common} />}
      {tab === 'feedback' && <FeedbackTab {...common} />}
      {tab === 'risks' && <RiskTab {...common} />}
      {tab === 'export' && <ExportTab {...common} />}
    </PoPShell>
  );
}
